import type { UnitTypeId } from '@obraplan/core/domain/shared/ids';
import type { FloorPlanKind } from '@obraplan/core/domain/takeoff/floor-plan';
import type { LinhaPorPeca } from '@obraplan/core/domain/takeoff/planilha-por-peca';
import { ehAreaUnica, nomeDaTipologia } from './area-unica';
import type { WorkspaceDraft } from './typology-draft';

/**
 * Para onde vai a planilha por peça (Fase 28, RN-392 — revista em 03/11/2026).
 *
 * A quantidade pronta é guardada numa tipologia, e não na obra: é a conta da tipologia que a
 * recebe, e é nela que a peça um dia será posicionada (etapa 2). A tipologia certa é a **área
 * comum** do desenho — a área única, na Fundação e na Contenção, onde as estacas, os blocos e o
 * baldrame já moram (RN-361). O apartamento e a vaga não recebem.
 *
 * **O desenho sai do atrelamento** (03/11/2026). A coluna ANDAR diz o desenho pelo nome
 * ("PAVIMENTO TIPO") ou o pavimento que o usa ("FACHADA"), e Pavimentos e desenhos diz quantos
 * andares o repetem — é por eles que a conta da área se multiplica. Não há escolha livre: a
 * Vila Munich importou o pavimento tipo inteiro como uma peça só, "PAVIMENTO", e uma obra de
 * teste, que só podia importar na Fundação, atrelou a Fundação aos andares do tipo; a fachada
 * dela entrou calada na mesma área e o orçamento a multiplicou por onze. Tudo o que não fecha
 * trava a importação, com o motivo.
 */

export interface DestinoPorPeca {
  readonly unitTypeId: UnitTypeId;
  /** "Área comum 1", "Área única da Fundação" — o nome que a tela mostra. */
  readonly rotulo: string;
  readonly floorPlanId?: string;
  readonly floorKind: FloorPlanKind;
}

/** Um pavimento da estrutura, atrelado ou não a um desenho em Pavimentos e desenhos. */
export interface PavimentoDaObra {
  readonly nome: string;
  readonly torre: string;
  readonly nivel: number;
  readonly floorPlanId?: string;
}

/** Para onde a planilha vai — ou por que ela não pode ir. */
export type DestinoDaPlanilha =
  | {
      readonly ok: true;
      /** O nome do desenho, como em Pavimentos e desenhos. */
      readonly desenho: string;
      /** O ANDAR como a planilha o escreve. */
      readonly andar: string;
      /** Os pavimentos atrelados ao desenho, de baixo para cima: o multiplicador. */
      readonly pavimentos: readonly PavimentoDaObra[];
      /** Os mesmos, citados por trechos seguidos (`citarPavimentos`). */
      readonly pavimentosCitados: string;
      /** As áreas do desenho. Com mais de uma, quem importa escolhe entre elas. */
      readonly areas: readonly DestinoPorPeca[];
    }
  | { readonly ok: false; readonly motivo: string };

/** Sem acento, sem caixa e sem espaço sobrando: "Fundação " é "FUNDACAO". */
function comparavel(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLocaleLowerCase('pt-BR');
}

/** As áreas da obra que podem receber a planilha: as únicas primeiro, depois as comuns. */
export function destinosPorPeca(
  obra: Pick<WorkspaceDraft, 'typologies'>,
): readonly DestinoPorPeca[] {
  const areasComuns = obra.typologies.filter((tipologia) => tipologia.kind === 'AREA_COMUM');
  return [
    ...areasComuns.filter(ehAreaUnica),
    ...areasComuns.filter((tipologia) => !ehAreaUnica(tipologia)),
  ].map((tipologia) => ({
    unitTypeId: tipologia.unitTypeId,
    rotulo: nomeDaTipologia(tipologia),
    ...(tipologia.floorPlanId === undefined ? {} : { floorPlanId: tipologia.floorPlanId }),
    floorKind: tipologia.floorKind,
  }));
}

// ── O ANDAR da planilha ──────────────────────────────────────────────────────────

interface AndarDaPlanilha {
  readonly comparavel: string;
  /** Como a planilha o escreveu na primeira vez, para a tela citar. */
  readonly escrito: string;
  readonly linhas: number;
}

/** Os ANDARes das linhas, do mais escrito ao menos. */
function andaresDaPlanilha(
  linhas: readonly Pick<LinhaPorPeca, 'andar'>[],
): readonly AndarDaPlanilha[] {
  const vezes = new Map<string, { readonly escrito: string; linhas: number }>();
  for (const linha of linhas) {
    const escrito = (linha.andar ?? '').trim().replace(/\s+/g, ' ');
    if (escrito === '') continue;
    const chave = comparavel(escrito);
    const atual = vezes.get(chave);
    if (atual === undefined) vezes.set(chave, { escrito, linhas: 1 });
    else atual.linhas += 1;
  }
  return [...vezes.entries()]
    .map(([chave, { escrito, linhas: quantas }]) => ({
      comparavel: chave,
      escrito,
      linhas: quantas,
    }))
    .sort((a, b) => b.linhas - a.linhas);
}

function quantos(n: number, um: string, varios: string): string {
  return n === 1 ? `1 ${um}` : `${String(n)} ${varios}`;
}

/** "linha 4", "linhas 4, 7 e 9", "linhas 4, 7, 9, 12, 15 e mais 3". */
function citarLinhas(numeros: readonly number[]): string {
  const CITADAS = 5;
  const vistos = numeros.slice(0, CITADAS).map(String);
  if (numeros.length === 1) return `linha ${vistos[0]!}`;
  if (numeros.length > CITADAS) {
    return `linhas ${vistos.join(', ')} e mais ${String(numeros.length - CITADAS)}`;
  }
  return `linhas ${vistos.slice(0, -1).join(', ')} e ${vistos.at(-1)!}`;
}

/**
 * Os pavimentos citados por trechos seguidos: "1º Pavimento a 5º Pavimento, 7º Pavimento a 12º
 * Pavimento". "1º a 12º" esconderia o 6º, que usa outro desenho — e é o número de pavimentos que
 * a pessoa confere. Com mais de uma torre, cada uma com o nome dela.
 *
 * `todos` são os pavimentos da obra, atrelados ou não: é contra eles que se vê o buraco.
 */
export function citarPavimentos(
  pavimentos: readonly PavimentoDaObra[],
  todos: readonly PavimentoDaObra[] = pavimentos,
): string {
  const escolhidos = new Set(pavimentos);
  const torres = [...new Set(pavimentos.map((pavimento) => pavimento.torre))];
  const porTorre = torres.map((torre) => {
    const daTorre = todos
      .filter((pavimento) => pavimento.torre === torre)
      .sort((a, b) => a.nivel - b.nivel);
    const trechos: PavimentoDaObra[][] = [];
    let atual: PavimentoDaObra[] = [];
    for (const pavimento of daTorre) {
      if (escolhidos.has(pavimento)) {
        atual.push(pavimento);
      } else if (atual.length > 0) {
        trechos.push(atual);
        atual = [];
      }
    }
    if (atual.length > 0) trechos.push(atual);
    const citados = trechos
      .map((trecho) =>
        trecho.length <= 2
          ? trecho.map((pavimento) => pavimento.nome).join(', ')
          : `${trecho[0]!.nome} a ${trecho.at(-1)!.nome}`,
      )
      .join(', ');
    return torres.length === 1 ? citados : `${torre}: ${citados}`;
  });
  return porTorre.join('; ');
}

type Falha = Extract<DestinoDaPlanilha, { readonly ok: false }>;

function falha(motivo: string): Falha {
  return { ok: false, motivo };
}

type Resolvido = { readonly ok: true; readonly floorPlanId: string } | Falha;

/** O desenho de um ANDAR: pelo nome do desenho, ou pelo pavimento que o usa. */
function resolverAndar(
  andar: AndarDaPlanilha,
  desenhos: ReadonlyMap<string, string>,
  pavimentos: readonly PavimentoDaObra[],
): Resolvido {
  const peloNome = [...desenhos].filter(([, nome]) => comparavel(nome) === andar.comparavel);
  if (peloNome.length > 1) {
    return falha(
      `Mais de um desenho se chama "${andar.escrito}" em Pavimentos e desenhos. Dê nomes diferentes a eles para a coluna ANDAR dizer qual recebe as peças.`,
    );
  }
  if (peloNome.length === 1) return { ok: true, floorPlanId: peloNome[0]![0] };

  const doAndar = pavimentos.filter((pavimento) => comparavel(pavimento.nome) === andar.comparavel);
  if (doAndar.length === 0) {
    const existentes = [...desenhos.values()].map((nome) => `"${nome}"`).join(', ');
    return falha(
      `Nenhum desenho nem pavimento da obra se chama "${andar.escrito}". A coluna ANDAR diz o desenho que recebe as peças, pelo nome que ele tem em Pavimentos e desenhos` +
        (existentes === '' ? ', e a obra ainda não tem desenho.' : `: ${existentes}.`),
    );
  }
  const solto = doAndar.find((pavimento) => pavimento.floorPlanId === undefined);
  if (solto !== undefined) {
    return falha(
      `O pavimento ${solto.nome} (${solto.torre}) não está atrelado a nenhum desenho em Pavimentos e desenhos. Atrele-o ao desenho dele e importe de novo.`,
    );
  }
  const ids = [...new Set(doAndar.map((pavimento) => pavimento.floorPlanId!))];
  if (ids.length > 1) {
    return falha(
      `"${andar.escrito}" é o nome de pavimentos atrelados a desenhos diferentes (${ids.map((id) => desenhos.get(id) ?? id).join(', ')}). Escreva no ANDAR o nome do desenho que recebe as peças.`,
    );
  }
  const id = ids[0]!;
  const doDesenho = pavimentos.filter((pavimento) => pavimento.floorPlanId === id);
  if (doDesenho.length > doAndar.length) {
    const desenho = desenhos.get(id) ?? id;
    return falha(
      `"${andar.escrito}" é um dos ${String(doDesenho.length)} pavimentos atrelados ao desenho ${desenho}. As peças entram na área do desenho, que vale para os ${String(doDesenho.length)}: a quantidade de "${andar.escrito}" contaria ${String(doDesenho.length)} vezes. Escreva no ANDAR o nome do desenho, "${desenho}", com a quantidade de um pavimento.`,
    );
  }
  return { ok: true, floorPlanId: id };
}

/**
 * O destino da planilha por peça, pelo atrelamento de Pavimentos e desenhos.
 *
 * Trava, com o motivo, quando: alguma linha não diz o ANDAR; o ANDAR não é desenho nem
 * pavimento da obra; o pavimento não tem desenho; o ANDAR é um pavimento entre vários do mesmo
 * desenho (a quantidade dele contaria em todos); a planilha mistura desenhos; o desenho não
 * tem pavimento atrelado ou não tem área comum; ou a Fundação está atrelada a mais de um
 * pavimento da mesma torre.
 */
export function destinoPeloAtrelamento(entrada: {
  readonly linhas: readonly Pick<LinhaPorPeca, 'andar' | 'linha'>[];
  readonly areas: readonly DestinoPorPeca[];
  /** id → nome de cada desenho da obra. */
  readonly desenhos: ReadonlyMap<string, string>;
  /** Todos os pavimentos da estrutura, atrelados ou não. */
  readonly pavimentos: readonly PavimentoDaObra[];
}): DestinoDaPlanilha {
  const { linhas, areas, desenhos, pavimentos } = entrada;

  const semAndar = linhas.filter((linha) => (linha.andar ?? '').trim() === '');
  if (semAndar.length > 0 && semAndar.length === linhas.length) {
    return falha(
      'A planilha não diz o ANDAR de nenhuma linha. É ele que diz o desenho que recebe as peças: preencha a coluna ANDAR com o nome do desenho, como está em Pavimentos e desenhos.',
    );
  }
  if (semAndar.length > 0) {
    return falha(
      `${quantos(semAndar.length, 'linha está', 'linhas estão')} sem ANDAR (${citarLinhas(semAndar.map((linha) => linha.linha))}). Sem ele não dá para saber o desenho que recebe a peça: preencha e importe de novo.`,
    );
  }

  const andares = andaresDaPlanilha(linhas);
  const porDesenho = new Map<string, AndarDaPlanilha[]>();
  for (const andar of andares) {
    const resolvido = resolverAndar(andar, desenhos, pavimentos);
    if (!resolvido.ok) return resolvido;
    const lista = porDesenho.get(resolvido.floorPlanId);
    if (lista === undefined) porDesenho.set(resolvido.floorPlanId, [andar]);
    else lista.push(andar);
  }

  if (porDesenho.size > 1) {
    const partes = [...porDesenho].map(([id, doDesenho]) => {
      const escritos = doDesenho.map((andar) => `"${andar.escrito}"`).join(', ');
      const total = doDesenho.reduce((soma, andar) => soma + andar.linhas, 0);
      return `${escritos} vai para o desenho ${desenhos.get(id) ?? id} (${quantos(total, 'linha', 'linhas')})`;
    });
    return falha(
      `A planilha mistura desenhos: ${partes.join('; ')}. Cada desenho tem a sua área e o seu número de pavimentos — importe uma planilha por desenho.`,
    );
  }

  const [id, doDesenho] = [...porDesenho][0]!;
  const desenho = desenhos.get(id) ?? doDesenho[0]!.escrito;
  const atrelados = pavimentos
    .filter((pavimento) => pavimento.floorPlanId === id)
    .sort((a, b) => a.nivel - b.nivel);
  if (atrelados.length === 0) {
    return falha(
      `O desenho ${desenho} não está atrelado a nenhum pavimento em Pavimentos e desenhos: a quantidade das peças não contaria em andar nenhum. Atrele os pavimentos que usam esse desenho e importe de novo.`,
    );
  }

  const doDesenhoAreas = areas.filter((area) => area.floorPlanId === id);
  if (doDesenhoAreas.length === 0) {
    return falha(
      `O desenho ${desenho} não tem área comum para receber as peças. A peça importada mora na área comum do desenho — o apartamento não a recebe. Crie a área comum no desenho ${desenho} e importe de novo.`,
    );
  }

  if (doDesenhoAreas.some((area) => area.floorKind === 'FUNDACAO')) {
    const porTorre = new Map<string, PavimentoDaObra[]>();
    for (const pavimento of atrelados) {
      porTorre.set(pavimento.torre, [...(porTorre.get(pavimento.torre) ?? []), pavimento]);
    }
    const repetida = [...porTorre].find(([, daTorre]) => daTorre.length > 1);
    if (repetida !== undefined) {
      const [torre, daTorre] = repetida;
      return falha(
        `O desenho ${desenho} é de fundação e está atrelado a ${String(daTorre.length)} pavimentos da ${torre} (${citarPavimentos(daTorre, pavimentos)}). A fundação acontece uma vez por torre, e a quantidade das peças contaria ${String(daTorre.length)} vezes. Corrija o atrelamento em Pavimentos e desenhos e importe de novo.`,
      );
    }
  }

  return {
    ok: true,
    desenho,
    andar: doDesenho[0]!.escrito,
    pavimentos: atrelados,
    pavimentosCitados: citarPavimentos(atrelados, pavimentos),
    areas: doDesenhoAreas,
  };
}
